/* ── Developer Portal ──────────────────────────────────────────────────────
 *
 * The customer-facing half of API product management: browse the catalogue,
 * read the docs, hold your credentials, try a call.
 *
 * Everything here talks to /api/portal/**, whose DTOs deliberately carry no
 * Keycloak group ids, no encrypted blobs and no upstream-provider fields. The
 * customer's credentials are Axon credentials; the gateway attaches the upstream
 * ones itself, so there is nothing sensitive on this page to leak.
 *
 * All component names in this file are unique to it. Static scripts share one
 * global scope, so a name repeated in index.html would silently shadow one of
 * the two definitions depending on load order.
 */

function portalQuotaLabel(q){
  if(!q||!q.enabled)return 'Unmetered';
  return q.limit.toLocaleString()+' calls / '+(q.interval>1?q.interval+' ':'')+q.unit.toLowerCase()+(q.interval>1?'s':'');
}

const PORTAL_LIFECYCLE_BADGE={PUBLISHED:'success',DEPRECATED:'warning',DRAFT:'gray',RETIRED:'error'};

function DeveloperPortalPage({showToast,confirm}){
  const [products,setProducts]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null);
  // Staff preview of the API catalogue. Customers get credentials through their
  // organisation (API Management -> Organisations), and see their own in the customer
  // portal; the old "viewing as" consumer picker and per-API subscribe went with the
  // ApiConsumer record (2026-10-17).

  const loadCatalogue=useCallback(async()=>{
    setLoading(true);
    try{
      const [cat]=await loadAll([
        ['Catalogue',API('GET','/api/portal/products'),[]],
      ],showToast);
      setProducts(Array.isArray(cat)?cat:[]);
    }catch(e){showToast&&showToast('Could not load the catalogue: '+e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);

  useEffect(()=>{loadCatalogue();},[loadCatalogue]);

  const subFor=()=>null;

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Developer Portal</h1>
          <div className="page-subtitle">The API catalogue and docs as customers see them. Grant access under Organisations.</div>
        </div>
      </div>

      {loading&&<div className="card"><div className="card-body">Loading catalogue…</div></div>}

      {!loading&&products.length===0&&(
        <div className="card"><div className="card-body empty-state" style={{textAlign:'center',padding:48}}>
          <h3 style={{margin:'0 0 8px'}}>Nothing published yet</h3>
          <p style={{color:'var(--text3)',margin:0}}>
            API products appear here once they are published and set to public visibility.
          </p>
        </div></div>
      )}

      {!loading&&!selected&&products.length>0&&(
        <PortalCatalog products={products} subFor={subFor}
                       onOpen={setSelected} onSubscribe={null}/>
      )}

      {selected&&(
        <PortalProductDetail
          summary={selected}
          subscription={subFor(selected.id)}
          onBack={()=>setSelected(null)}
          onSubscribe={null}
          onRotate={null}
          onOpenVersion={v=>{
            // The switcher carries a slug, not an id. Prefer the catalogue entry so the
            // detail page still resolves this customer's subscription — `subFor` keys on
            // the product id, and a slug would silently look unsubscribed.
            const known=products.find(x=>x.slug===v.slug);
            setSelected(known||{id:v.slug,slug:v.slug,name:v.apiVersion});
          }}
          showToast={showToast}/>
      )}

    </div>
  );
}

function PortalCatalog({products,subFor,onOpen,onSubscribe}){
  return(
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:16}}>
      {products.map(p=>{
        const sub=subFor(p.id);
        return(
          <div key={p.id} className="card" style={{display:'flex',flexDirection:'column'}}>
            <div className="card-body" style={{flex:1}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                <div style={{fontWeight:600,fontSize:16,display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                  <span style={{width:10,height:10,borderRadius:3,background:p.color||'var(--brand-start)',flex:'none'}}></span>
                  {p.name}
                  {p.type==='AI'&&<span className="badge badge-info" style={{fontSize:10,flex:'none'}}>AI</span>}
                </div>
                <span className={'badge badge-'+(PORTAL_LIFECYCLE_BADGE[p.lifecycle]||'gray')}
                      style={{fontSize:10,flex:'none'}}>{p.lifecycle}</span>
              </div>
              <p style={{fontSize:13,color:'var(--text2)',marginBottom:12}}>{p.description}</p>
              <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
                <PortalVersionChip product={p}/>
              </div>
              <div style={{display:'flex',gap:14,fontSize:11,color:'var(--text3)',flexWrap:'wrap'}}>
                <span>v{p.version}</span>
                {p.type==='AI'
                  ?<span>{(p.models||[]).length} model{(p.models||[]).length===1?'':'s'}</span>
                  :<span>{p.operationCount} operation{p.operationCount===1?'':'s'}</span>}
                <span>{p.type==='AI'?aiTokenQuotaLabel(p.tokenQuota):portalQuotaLabel(p.quota)}</span>
              </div>
              {p.type==='AI'&&(p.models||[]).length>0&&(
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:10}}>
                  {p.models.slice(0,4).map(m=>(
                    <span key={m} className="badge badge-gray"
                          style={{fontSize:10,fontFamily:'monospace'}}>{m}</span>
                  ))}
                  {p.models.length>4&&<span style={{fontSize:10,color:'var(--text3)'}}>+{p.models.length-4}</span>}
                </div>
              )}
            </div>
            <div className="card-body" style={{borderTop:'1px solid var(--border)',display:'flex',
                                               gap:8,alignItems:'center',paddingTop:12}}>
              <button className="btn btn-sm btn-secondary" onClick={()=>onOpen(p)}>View docs</button>
              {!sub&&onSubscribe&&<button className="btn btn-sm btn-primary" onClick={()=>onSubscribe(p)}>Subscribe</button>}
              {sub&&<span className="badge badge-success" style={{fontSize:10}}>
                {sub.status==='APPROVED'?'Subscribed':sub.status}
              </span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PortalProductDetail({summary,subscription,onBack,onSubscribe,onRotate,onOpenVersion,showToast}){
  const [product,setProduct]=useState(null);
  const [tab,setTab]=useState('docs');

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const d=await API('GET','/api/portal/products/'+summary.id);
        if(!cancelled)setProduct(d);
      }catch(e){showToast&&showToast(e.message,'error');}
    })();
    return()=>{cancelled=true;};
  },[summary.id,showToast]);

  if(!product)return <div className="card"><div className="card-body">Loading…</div></div>;

  // An AI product sells models, not HTTP operations: its single operation is the
  // chat endpoint, so an Operations table would be one uninformative row and the
  // generic Try-it form cannot express a prompt.
  const isAi=product.type==='AI';

  const docsHtml=()=>{
    try{return{__html:window.marked?window.marked.parse(product.documentation||''):''};}
    catch(e){return{__html:''};}
  };

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <button className="btn btn-sm btn-ghost" onClick={onBack}>← Catalogue</button>
        <div style={{fontWeight:600,fontSize:18,display:'flex',alignItems:'center',gap:8}}>
          <span style={{width:10,height:10,borderRadius:3,background:product.color||'var(--brand-start)'}}></span>
          {product.name}
        </div>
        <span className="badge badge-gray" style={{fontSize:10}}>v{product.version}</span>
        {product.apiVersion&&(
          <span className="badge badge-gray"
                style={{fontSize:10,fontFamily:'monospace'}}>{product.apiVersion}</span>
        )}
        <span className={'badge badge-'+(PORTAL_LIFECYCLE_BADGE[product.lifecycle]||'gray')}
              style={{fontSize:10}}>{product.lifecycle}</span>
      </div>

      <PortalVersionBanner product={product}/>

      <div style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap'}}>
        <div style={{flex:'1 1 520px',minWidth:0}}>
          <div className="um-tabs" style={{marginBottom:16}}>
            {(isAi
              ?[['docs','Documentation'],['ai','Models & playground'],['spec','API reference']]
              :[['docs','Documentation'],['operations','Operations'],['spec','API reference'],['tryit','Try it']]
            ).map(([id,label])=>(
              <button key={id} className={'um-tab'+(tab===id?' active':'')} onClick={()=>setTab(id)}>{label}</button>
            ))}
          </div>

          {tab==='ai'&&<PortalAiPanel product={product} subscription={subscription}/>}

          {tab==='docs'&&(
            <div className="card"><div className="card-body markdown-body" dangerouslySetInnerHTML={docsHtml()}/></div>
          )}

          {tab==='operations'&&(
            <div className="card"><div className="card-body" style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                    <th style={{padding:'8px 6px'}}>Operation</th>
                    <th style={{padding:'8px 6px'}}>Methods</th>
                    <th style={{padding:'8px 6px'}}>Path</th>
                  </tr>
                </thead>
                <tbody>
                  {(product.operations||[]).map((o,i)=>(
                    <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                      <td style={{padding:'10px 6px'}}>
                        <div style={{fontWeight:500}}>
                          {o.name}
                          {o.deprecated&&<span className="badge badge-warning" style={{fontSize:9,marginLeft:6}}>deprecated</span>}
                        </div>
                        <div style={{fontSize:11,color:'var(--text3)'}}>{o.summary||''}</div>
                      </td>
                      <td style={{padding:'10px 6px',fontSize:11}}>{(o.methods||[]).join(', ')}</td>
                      <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:11}}>{o.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></div>
          )}

          {tab==='spec'&&<PortalSwagger productId={product.id}/>}

          {tab==='tryit'&&<PortalTryIt product={product} subscription={subscription}/>}
        </div>

        <div style={{flex:'0 1 320px',minWidth:280}}>
          <PortalCredentials product={product} subscription={subscription}
                             onSubscribe={onSubscribe} onRotate={onRotate}/>
          <PortalVersionPinCard product={product}/>
          <PortalVersionSwitcher product={product} onOpenVersion={v=>onOpenVersion&&onOpenVersion(v)}/>
        </div>
      </div>
    </div>
  );
}

function PortalCredentials({product,subscription,onSubscribe,onRotate}){
  const [copied,setCopied]=useState(false);
  const copy=(v)=>navigator.clipboard.writeText(v).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),1500);});
  return(
    <div className="card">
      <div className="card-header"><div className="card-title">Your credentials</div></div>
      <div className="card-body">
        {!subscription&&!onSubscribe&&(
          <p style={{fontSize:13,color:'var(--text2)',margin:0}}>
            Customers get credentials for this API through an API product granted to their organisation:
            API Management → Organisations → the organisation → its app → New credential.
          </p>
        )}
        {!subscription&&onSubscribe&&(
          <div>
            <p style={{fontSize:13,color:'var(--text2)',marginTop:0}}>
              You are not subscribed to {product.name} yet.
            </p>
            <button className="btn btn-primary btn-sm" onClick={onSubscribe}>Request access</button>
            <p style={{fontSize:11,color:'var(--text3)',marginBottom:0,marginTop:10}}>
              {product.approvalMode==='AUTO'
                ? 'Credentials are issued immediately.'
                : 'An operator reviews the request before credentials are issued.'}
            </p>
          </div>
        )}

        {subscription&&subscription.status!=='APPROVED'&&(
          <div>
            <span className="badge badge-warning" style={{fontSize:10}}>{subscription.status}</span>
            <p style={{fontSize:13,color:'var(--text2)'}}>
              {subscription.status==='PENDING'
                ? 'Your request is waiting for approval.'
                : (subscription.notes||'This subscription is not currently active.')}
            </p>
          </div>
        )}

        {subscription&&subscription.status==='APPROVED'&&(
          <div>
            <div className="form-group">
              <label className="form-label">Client ID</label>
              <div style={{display:'flex',gap:6}}>
                <input className="form-input" readOnly value={subscription.axonClientId||''}
                       style={{fontFamily:'monospace',fontSize:11}}/>
                <button className="btn btn-sm btn-secondary" onClick={()=>copy(subscription.axonClientId)}>
                  {copied?'✓':'Copy'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Client secret</label>
              <input className="form-input" readOnly value="••••••••" style={{fontFamily:'monospace'}}/>
              <p style={{fontSize:11,color:'var(--text3)',marginTop:6,marginBottom:0}}>
                Shown only once, when it is issued. If you have lost it, rotate to get a new one —
                the old secret stops working the moment you do.
              </p>
            </div>
            <button className="btn btn-sm btn-secondary" onClick={()=>onRotate(subscription)}>Rotate secret</button>

            <div style={{marginTop:16,paddingTop:12,borderTop:'1px solid var(--border)',fontSize:12}}>
              <div style={{color:'var(--text3)'}}>Token endpoint</div>
              <div style={{fontFamily:'monospace',fontSize:11,wordBreak:'break-all'}}>{product.tokenPath}</div>
              <div style={{color:'var(--text3)',marginTop:8}}>Plan</div>
              <div>{portalQuotaLabel(product.quota)}</div>
              {product.rateLimit&&product.rateLimit.enabled&&(
                <div style={{color:'var(--text3)',fontSize:11,marginTop:2}}>
                  burst {product.rateLimit.rate}/s, up to {product.rateLimit.burst}
                </div>
              )}
            </div>
          </div>
        )}

        {(product.contactEmail||product.termsUrl)&&(
          <div style={{marginTop:16,paddingTop:12,borderTop:'1px solid var(--border)',fontSize:12}}>
            {product.contactEmail&&<div>Support: <a href={'mailto:'+product.contactEmail}>{product.contactEmail}</a></div>}
            {product.termsUrl&&<div><a href={product.termsUrl} target="_blank" rel="noreferrer">Terms of use</a></div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* Swagger UI is loaded on demand from the same CDN the app already uses for
   js-yaml and marked. Pointing the bundled springdoc UI at an arbitrary spec URL
   would need springdoc.swagger-ui.queryConfigEnabled, which on a public endpoint
   lets any visitor render any URL — not worth it for one embedded view. */
function PortalSwagger({productId}){
  const containerId='portal-swagger-'+productId;
  const [status,setStatus]=useState('loading');

  useEffect(()=>{
    let cancelled=false;

    const ensureAsset=(tag,attrs)=>new Promise((resolve,reject)=>{
      const key=attrs.src||attrs.href;
      if(document.querySelector(tag+'[data-portal-asset="'+key+'"]')){resolve();return;}
      const el=document.createElement(tag);
      Object.keys(attrs).forEach(k=>el.setAttribute(k,attrs[k]));
      el.setAttribute('data-portal-asset',key);
      el.onload=resolve;el.onerror=()=>reject(new Error('Could not load '+key));
      document.head.appendChild(el);
    });

    (async()=>{
      try{
        await ensureAsset('link',{rel:'stylesheet',
          href:'https://cdn.jsdelivr.net/npm/swagger-ui-dist@5.17.14/swagger-ui.css'});
        await ensureAsset('script',{
          src:'https://cdn.jsdelivr.net/npm/swagger-ui-dist@5.17.14/swagger-ui-bundle.js'});
        if(cancelled)return;
        if(!window.SwaggerUIBundle)throw new Error('Swagger UI did not initialise');
        window.SwaggerUIBundle({
          url:'/api/portal/products/'+productId+'/openapi',
          dom_id:'#'+containerId,
          deepLinking:false,
          docExpansion:'list',
          defaultModelsExpandDepth:-1,
          tryItOutEnabled:false
        });
        setStatus('ready');
      }catch(e){
        if(!cancelled)setStatus('error');
      }
    })();

    return()=>{cancelled=true;};
  },[productId,containerId]);

  return(
    <div className="card">
      <div className="card-body">
        {status==='loading'&&<div style={{color:'var(--text3)',fontSize:13}}>Loading API reference…</div>}
        {status==='error'&&(
          <div style={{fontSize:13}}>
            <p>The interactive reference could not load.</p>
            <a href={'/api/portal/products/'+productId+'/openapi'} target="_blank" rel="noreferrer">
              Download the OpenAPI document instead
            </a>
          </div>
        )}
        <div id={containerId}></div>
      </div>
    </div>
  );
}

/* Deliberately uses raw fetch, not the app's API() helper. PROXY_PATHS already
   covers the gateway path families, so API() would attach the *operator's*
   session token — the exact opposite of what a customer is testing. The token
   obtained here is held in component state and never written to localStorage:
   it is a customer credential, not part of the operator's session. */
function PortalTryIt({product,subscription}){
  const ops=(product.operations||[]).filter(o=>!o.authEndpoint);
  const [clientId,setClientId]=useState(subscription?subscription.axonClientId||'':'');
  const [clientSecret,setClientSecret]=useState('');
  const [token,setToken]=useState('');
  const [opIndex,setOpIndex]=useState(0);
  const [method,setMethod]=useState(ops.length?(ops[0].methods||['GET'])[0]:'GET');
  const [pathOverride,setPathOverride]=useState(ops.length?ops[0].path:'');
  const [body,setBody]=useState('');
  const [result,setResult]=useState(null);
  const [busy,setBusy]=useState('');

  const chooseOp=(i)=>{
    setOpIndex(i);
    const o=ops[i];
    if(o){setPathOverride(o.path);setMethod((o.methods||['GET'])[0]);}
  };

  const getToken=async()=>{
    setBusy('token');setResult(null);
    try{
      const r=await fetch(product.tokenPath,{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({grant_type:'client_credentials',client_id:clientId,client_secret:clientSecret})
      });
      const d=await r.json();
      if(!r.ok){setResult({status:r.status,body:JSON.stringify(d,null,2)});return;}
      setToken(d.access_token||'');
      setResult({status:r.status,body:'Token acquired, valid for '+(d.expires_in||'?')+'s'});
    }catch(e){setResult({status:0,body:String(e)});}
    finally{setBusy('');}
  };

  const send=async()=>{
    setBusy('call');
    try{
      const opts={method:method,headers:{'Authorization':'Bearer '+token}};
      if(method!=='GET'&&method!=='HEAD'&&body.trim()){
        opts.headers['Content-Type']='application/json';
        opts.body=body;
      }
      const r=await fetch(pathOverride,opts);
      const text=await r.text();
      let pretty=text;
      try{pretty=JSON.stringify(JSON.parse(text),null,2);}catch(e){}
      setResult({status:r.status,body:pretty});
    }catch(e){setResult({status:0,body:String(e)});}
    finally{setBusy('');}
  };

  if(ops.length===0){
    return <div className="card"><div className="card-body" style={{color:'var(--text3)'}}>
      This product has no callable operations.
    </div></div>;
  }

  return(
    <div className="card"><div className="card-body">
      <div style={{fontWeight:600,marginBottom:4}}>1. Get an access token</div>
      <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
        Your Axon credentials. Nothing here is stored — the token lives in this page only.
      </p>
      <div style={{display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap',marginBottom:16}}>
        <div className="form-group" style={{margin:0,flex:'1 1 200px'}}>
          <label className="form-label">Client ID</label>
          <input className="form-input" value={clientId} onChange={e=>setClientId(e.target.value)}
                 style={{fontFamily:'monospace',fontSize:12}}/>
        </div>
        <div className="form-group" style={{margin:0,flex:'1 1 200px'}}>
          <label className="form-label">Client secret</label>
          <input className="form-input" type="password" value={clientSecret}
                 onChange={e=>setClientSecret(e.target.value)}/>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={getToken}
                disabled={busy==='token'||!clientId||!clientSecret}>
          {busy==='token'?'…':'Get token'}
        </button>
      </div>
      {token&&<div className="badge badge-success" style={{fontSize:10,marginBottom:14,display:'inline-block'}}>
        Token held for this page
      </div>}

      <div style={{fontWeight:600,marginBottom:8}}>2. Call an operation</div>
      <div style={{display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
        <div className="form-group" style={{margin:0,flex:'1 1 240px'}}>
          <label className="form-label">Operation</label>
          <select className="form-input" value={opIndex} onChange={e=>chooseOp(parseInt(e.target.value,10))}>
            {ops.map((o,i)=><option key={i} value={i}>{o.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{margin:0,width:110}}>
          <label className="form-label">Method</label>
          <select className="form-input" value={method} onChange={e=>setMethod(e.target.value)}>
            {(ops[opIndex]?ops[opIndex].methods||['GET']:['GET']).map(m=><option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Path</label>
        <input className="form-input" value={pathOverride} onChange={e=>setPathOverride(e.target.value)}
               style={{fontFamily:'monospace',fontSize:12}}
               placeholder="Replace any {placeholder} with a real value"/>
      </div>
      {method!=='GET'&&(
        <div className="form-group">
          <label className="form-label">Request body (JSON)</label>
          <textarea className="form-input" rows={6} value={body} onChange={e=>setBody(e.target.value)}
                    style={{fontFamily:'monospace',fontSize:12}}/>
        </div>
      )}
      <button className="btn btn-primary btn-sm" onClick={send} disabled={busy==='call'||!token}>
        {busy==='call'?'Sending…':'Send request'}
      </button>
      {!token&&<span style={{fontSize:12,color:'var(--text3)',marginLeft:10}}>Get a token first</span>}

      {result&&(
        <div style={{marginTop:16}}>
          <div style={{fontSize:12,marginBottom:6}}>
            <span className={'badge badge-'+(result.status>=200&&result.status<300?'success':
                                             result.status>=400&&result.status<500?'warning':'error')}
                  style={{fontSize:10}}>
              {result.status||'network error'}
            </span>
          </div>
          <pre style={{background:'var(--surface2)',padding:12,borderRadius:8,maxHeight:340,
                       overflow:'auto',fontSize:11,margin:0}}>{result.body}</pre>
        </div>
      )}
    </div></div>
  );
}

function PortalSecretModal({payload,onClose}){
  const [copied,setCopied]=useState('');
  if(!payload)return null;
  const copy=(what,value)=>navigator.clipboard.writeText(value)
    .then(()=>{setCopied(what);setTimeout(()=>setCopied(''),1500);});
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:560}}>
        <div className="modal-header">
          <div className="modal-title">Your Axon credentials</div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body"><div className="modal-body-scroll">
          <div className="badge badge-warning" style={{marginBottom:14,display:'inline-block'}}>
            Shown once — copy it now
          </div>
          <p style={{fontSize:13,color:'var(--text2)',marginTop:0}}>
            {payload.warning||'This is the only time this secret is shown.'}
          </p>
          <div className="form-group">
            <label className="form-label">Client ID</label>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" readOnly value={payload.clientId||''}
                     style={{fontFamily:'monospace',fontSize:12}}/>
              <button className="btn btn-sm btn-secondary" onClick={()=>copy('id',payload.clientId)}>
                {copied==='id'?'Copied':'Copy'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Client secret</label>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" readOnly value={payload.clientSecret||''}
                     style={{fontFamily:'monospace',fontSize:12}}/>
              <button className="btn btn-sm btn-secondary" onClick={()=>copy('secret',payload.clientSecret)}>
                {copied==='secret'?'Copied':'Copy'}
              </button>
            </div>
          </div>
        </div></div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
